<script lang="ts">
import { computed, defineComponent, reactive, toRaw, toRefs } from 'vue'
import { useToast } from 'vue-toastification'
import hash from 'object-hash'
import omit from 'lodash/omit'
import { useEnhancer } from '@/app/enhancer'
import { useCustomization } from '@/hooks/useCustomization'
import { useGtm } from '@/hooks/useGtm'
import { useDevices } from '@/hooks/useDevices'
import { EventType, useEvents } from '@/hooks/useEvents'
import { isProd } from '@/configs/environment'
import switchIconPath from '@/assets/svg/switch-camera-icon.svg?url'
import ShowPageIcon from '@/assets/svg/show-page-icon.svg?component'
import ChatBotIcon from '@/assets/svg/chatbot_icon.svg?component'
import ShowMyListIcon from '@/assets/svg/show-my-list-icon.svg?component'
import AddToMyListIcon from '@/assets/svg/add-to-my-list-icon.svg?component'
import snsWhiteIconPath from '@/assets/svg/sns-icon.svg?url'
import snsBlackIconPath from '@/assets/svg/sns-black-icon.svg?url'
import TheRandomButton from '@/components/ui/share/RandomButton.vue'

export default defineComponent({
  name: 'RightMenuItems',
  components: { ShowPageIcon, ChatBotIcon, AddToMyListIcon, ShowMyListIcon, TheRandomButton },
  props: {
    isTryOn: {
      type: Boolean,
      default: () => false,
    },
    contrastStyle: {
      type: String,
      default: () => '',
    },
    floatClassName: {
      type: String,
      default: () => '',
    },
  },
  setup(props) {
    const { storeFavorites } = useEnhancer()
    const customization = useCustomization()
    const toast = useToast()
    const { isMobile, isDesktop } = useDevices()
    const eventBus = useEvents()

    const { pushGtm, pushPixel } = useGtm()
    const trackingRefs = reactive({
      refResetDefault: null as any,
      refAddToMyList: null as any,
      refShowMyList: null as any,
      refShareBySns: null as any,
      refShowPages: null as any,
      refShowChatbot: null as any,
      refScreenshot: null as any,
      refShareLine: null as any,
      refQuestionnaire: null as any,
      showMenus: true,
    })

    const renderIconColor = computed(() =>
      props.contrastStyle === 'white' ? 'dark' : 'white',
    )

    // const renderTextColor = computed(() =>
    //   props.contrastStyle === 'white' ? 'white' : 'dark',
    // )

    const isNotAvailable = computed(
      () => storeFavorites.selectedModel?.isOnDev,
    )

    const snsIconPath = computed(() =>
      props.isTryOn ? snsWhiteIconPath : snsBlackIconPath,
    )

    // const showPageIconPath = computed(() =>
    //   props.isTryOn ? showPageWhiteIcon : showPageBlackIcon,
    // )

    const shareWithLine = () => {
      eventBus.emit(EventType.SHARE_WITH_LINE)
      pushGtm(trackingRefs.refShareLine?.$el)
      pushPixel(trackingRefs.refShareLine?.$el)
    }

    const isDirty = computed(() => {
      const { glassesVariant } = toRaw(storeFavorites.addToCartVariant)
      const inputVariant = toRaw(customization.getInputVariant.value)
      return (
        hash(omit(glassesVariant, ['thumbnailUrl']))
        === hash(omit(inputVariant, ['thumbnailUrl']))
      )
    })

    const promptMessage = () => {
      toast.info('この機能はまだ利用できません。')
    }

    const addToMyList = () => {
      if (customization.getCurrentDemoMode.value)
        return promptMessage()

      eventBus.emit(EventType.ADD_TO_MY_LIST)
      pushGtm(trackingRefs.refAddToMyList?.$el)
    }

    const resetDefault = () => {
      eventBus.emit(EventType.RESET_DEFAULT)
      pushGtm(trackingRefs.refResetDefault?.$el)
    }

    const shareBySns = () => {
      eventBus.emit(EventType.SHARE_BY_SNS)
      pushGtm(trackingRefs.refShareBySns?.$el)
    }

    const showPages = () => {
      eventBus.emit(EventType.SHOW_PAGES)
      pushGtm(trackingRefs.refShowPages?.$el)
    }

    const showMyList = () => {
      eventBus.emit(EventType.SHOW_MY_LIST, !isDirty.value)
      pushGtm(trackingRefs.refShowMyList?.$el)
    }

    const screenShot = () => {
      eventBus.emit(EventType.SCREEN_SHOT)
      pushGtm(trackingRefs.refScreenshot?.$el)
    }

    const shareQuestion = () => {
      eventBus.emit(EventType.SHARE_QUESTIONNAIRE)
      pushGtm(trackingRefs.refQuestionnaire?.$el)
    }

    const showChatbot = () => {
      eventBus.emit(EventType.SHOW_CHATBOT)
      pushGtm(trackingRefs.refShowChatbot?.$el)
    }

    return {
      ...toRefs(trackingRefs),
      isMobile,
      isDesktop,
      isProd,
      renderIconColor,
      // renderTextColor,
      isNotAvailable,
      isDirty,
      switchIconPath,
      isDemoMode: customization.getCurrentDemoMode,
      addToMyList,
      resetDefault,
      shareBySns,
      showPages,
      showMyList,
      screenShot,
      shareWithLine,
      shareQuestion,
      showChatbot,
      snsIconPath,
      // showPageIconPath,
    }
  },
})
</script>

<template>
  <div class="column" style="min-width: 78px">
    <div class="relative-position text-center q-mb-lg" :class="[floatClassName]">
      <q-btn
        ref="refResetDefault"
        :text-color="contrastStyle === 'white' ? 'dark' : 'white'"
        :color="contrastStyle"
        round
        size="16px"
        padding="xs"
        icon="refresh"
        glasson-click="menu_reset_button"
        :disable="isDirty"
        @click="resetDefault"
      />
      <span
        class="text-small text-shadow absolute full-width text-no-wrap" :class="[
          `text-${contrastStyle}`,
        ]"
        style="bottom: 0px; left: 50%; transform: translate(-50%, 20px)"
        @click="resetDefault"
      >
        リセット
      </span>
    </div>
    <div
      v-if="!isDemoMode"
      class="relative-position text-center q-mb-md" :class="floatClassName"
    >
      <q-btn
        ref="refAddToMyList"
        :text-color="contrastStyle"
        :disable="isNotAvailable"
        round
        flat
        glasson-click="menu_save_button"
        size="16px"
        @click="addToMyList"
      >
        <AddToMyListIcon style="transform: scale(1);" />
      </q-btn>
      <span
        class="text-small text-shadow absolute full-width" :class="`text-${contrastStyle}`"
        style="bottom: 0px; left: 50%; transform: translate(-50%, 10px)"
        @click="addToMyList"
      >
        カートに保存
      </span>
    </div>
    <div
      v-if="!isDemoMode"
      class="relative-position text-center q-mb-lg" :class="[floatClassName]"
    >
      <q-btn
        ref="refShowMyList"
        :text-color="contrastStyle"
        round
        flat
        glasson-click="menu_my_list_button"
        size="16px"
        @click="showMyList"
      >
        <ShowMyListIcon style="transform: scale(1);" />
      </q-btn>
      <div
        class="text-small text-shadow absolute full-width text-no-wrap" :class="[
          `text-${contrastStyle}`,
        ]"
        style="bottom: 0px; left: 50%; transform: translate(-50%, 10px)"
        @click="showMyList"
      >
        カートを見る
      </div>
    </div>
    <div v-if="!isDemoMode" class="relative-position text-center q-mb-lg" :class="[floatClassName]">
      <q-btn
        ref="refShowChatbot"
        type="a"
        glasson-click="menu_show_chatbot"
        href="javascript:void(0)"
        onclick="ChatplusScript.openChat()"
        flat
        round
        size="16px"
        :text-color="contrastStyle"
        @click="showChatbot"
      >
        <ChatBotIcon style="transform: scale(0.8);" />
      </q-btn>
      <a
        href="javascript:void(0)"
        onclick="ChatplusScript.openChat()"
        class="text-small text-shadow absolute full-width" :class="[`text-${contrastStyle}`]"
        style="bottom: -16px; left: 50%; transform: translate(-50%, 10px); text-decoration: none;"
        @click="showChatbot"
      >
        チャットで相談 (自動対応)
      </a>
    </div>

    <div v-if="!isDemoMode" class="relative-position text-center q-mt-sm q-mb-md" :class="[floatClassName]">
      <q-btn
        ref="refShowPages"
        :text-color="contrastStyle"
        glasson-click="menu_show_pages_button"
        flat
        round
        size="16px"
        @click="showPages"
      >
        <ShowPageIcon style="transform: scale(0.8);" />
      </q-btn>
      <span
        class="text-small text-shadow absolute full-width" :class="[
          `text-${contrastStyle}`,
        ]"
        style="bottom: 0px; left: 50%; transform: translate(-50%, 10px)"
        @click="showPages"
      >
        ページを見る
      </span>
    </div>

    <!-- <div class="relative-position text-center q-mb-md" :class="[floatClassName]">
      <q-btn
        ref="refScreenshot"
        :text-color="contrastStyle"
        flat
        round
        size="16px"
        glasson-click="menu_photo_button"
        icon="photo_camera"
        @click="screenShot"
      />
      <span
        class="text-small text-shadow absolute full-width" :class="[
          `text-${contrastStyle}`,
        ]"
        style="bottom: -12px; left: 50%; transform: translate(-50%, 10px)"
        @click="screenShot"
      >
        タップで<br>撮影
      </span>
    </div> -->
    <div
      v-if="isDemoMode"
      class="relative-position text-center q-mt-sm q-mb-md" :class="[floatClassName]"
    >
      <TheRandomButton :contrast-style="contrastStyle" />
    </div>

    <div
      v-if="isDemoMode"
      class="relative-position text-center q-mt-sm q-mb-md" :class="[floatClassName]"
    >
      <q-btn
        ref="refShareLine"
        :text-color="contrastStyle"
        flat
        round
        glasson-click="menu_line_button"
        dense
        size="lg"
        class="q-mb-xs"
        icon="img:https://img.icons8.com/color/344/line-me.png"
        @click="shareWithLine"
      />
      <span
        class="text-small text-shadow absolute full-width" :class="[
          `text-${contrastStyle}`,
        ]"
        style="bottom: -16px; left: 50%; transform: translate(-50%, 7px)"
        @click="shareWithLine"
      >
        このデザインで<br>LINE購入
      </span>
    </div>

    <div class="relative-position text-center q-mt-sm" :class="[floatClassName]">
      <q-btn
        ref="refShareBySns"
        :text-color="contrastStyle"
        glasson-click="menu_share_button"
        flat
        round
        size="16px"
        :icon="`img:${snsIconPath}`"
        @click="shareBySns"
      />
      <span
        class="text-small text-shadow absolute full-width" :class="[
          `text-${contrastStyle}`,
        ]"
        style="bottom: -16px; left: 50%; transform: translate(-50%, 7px)"
        @click="shareBySns"
      >
        デザインをシェアする
      </span>
    </div>
    <!-- <div
      v-if="isDemoMode"
      :class="['relative-position text-center q-mt-sm', floatClassName]"
    >
      <q-btn
        :text-color="contrastStyle"
        flat
        round
        ref="refQuestionnaire"
        glasson-click="menu_questionnaire_button"
        dense
        size="16px"
        class="q-mb-xs"
        :icon="`img:${editIconPath}`"
        @click="shareQuestion"
      />
      <span
        :class="[
          'text-small text-shadow absolute full-width',
          `text-${contrastStyle}`,
        ]"
        style="bottom: -16px; left: 50%; transform: translate(-50%, 7px)"
        @click="shareQuestion"
      >
        エントリーは<br />
        こちらから
      </span>
    </div> -->
  </div>
</template>
